import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {themeConstants} from '../../../../../libs/shared/ui/src/theme/themeConstants';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {StartGuideProgressBar} from '../../../startGuide/startGuideProgressBar';

interface GuideButtonProps {
  numCompletedItems: number;
  numTotalItems: number;
  isSelected?: boolean;
}

interface StyledGuideButton {
  $isSelected: boolean;
}

const StyledGuideButton = styled(ChromelessButton)<StyledGuideButton>`
  height: 100%;
  padding: 4px 12px 4px 18px;
  border-radius: 100px;

  display: grid;
  grid-template-areas: 'content . chevron';
  grid-template-columns: 1fr 4px 20px;
  align-items: center;
  margin-right: -4px;

  &:hover {
    ${(p) => addHoverStyles(p.$isSelected, p.theme)}
  }

  ${(p) => maybeAddSelectedStyles(p.$isSelected, p.theme)}
`;

function addHoverStyles($isSelected: boolean, theme: UserTheme) {
  // If button is selected, don't add hover styles.
  if ($isSelected) {
    return css``;
  }
  return css`
    background-color: ${theme.topBar.buttonHoverBackground};
  `;
}

function maybeAddSelectedStyles($isSelected: boolean, theme: UserTheme) {
  if ($isSelected) {
    return css`
      background: ${theme.topBar.buttonSelectedBackground};
      box-shadow: 0px 1px 3px ${theme.alphas.white20};
      z-index: 1;
      position: relative;
    `;
  }

  return null;
}

interface StyledTextWrapperSpanProps {
  $longText: string;
  $shortText: string;
}

const StyledTextWrapperSpan = styled.span<StyledTextWrapperSpanProps>`
  &:before {
    content: ${(p) => `"${p.$longText}"`};
    white-space: nowrap;
  }
  @media (max-width: 1025px) {
    &:before {
      content: ${(p) => `"${p.$shortText}"`};
    }
  }

  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  color: ${themeConstants.white};
`;

const StyledContentContainerDiv = styled.div`
  height: 100%;
  gap: 4px;

  grid-area: content;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledChevronIcon = styled(Icon)`
  grid-area: chevron;
  color: ${themeConstants.white};
  justify-self: center;
`;

export const GuideButton: FC<GuideButtonProps> = ({isSelected, numCompletedItems, numTotalItems}) => {
  const localize = useLocalize();
  const longText = localize(
    defineMessage({
      id: 'guide-button-setup-guide',
      message: 'Setup guide',
    }),
  );
  const shortText = localize(
    defineMessage({
      id: 'guide-button-setup',
      message: 'Setup',
    }),
  );
  return (
    <StyledGuideButton $isSelected={Boolean(isSelected)}>
      <StyledContentContainerDiv>
        <StyledTextWrapperSpan $longText={longText} $shortText={shortText} />
        <StartGuideProgressBar completedSteps={numCompletedItems} totalSteps={numTotalItems} isBanner />
      </StyledContentContainerDiv>
      <StyledChevronIcon name="chevronDownSmall" />
    </StyledGuideButton>
  );
};
